/// <reference types="vite-plugin-svgr/client" />

import Image from 'next/image';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GateSelector } from '../../../features/parkings/GateSelector';
import { LatestEventQuery } from '../../../graphql/events/latestEventQuery';
import { GetParkingByIdQuery } from '../../../graphql/parkings/getParkingByIdQuery';
import { GetParkingsWithinDistance } from '../../../graphql/parkings/getParkingsWithinDistance';
import { RequestAccessToParkingMutation } from '../../../graphql/parkings/requestAccessToParkingMutation';
import { UnlockParkingByIdMutation } from '../../../graphql/parkings/unlockParkingByIdMutation';
import { SubscribeToPlanMutation } from '../../../graphql/stripe/subscriptionPlan/subscribeToPlanMutation';
import { StartParkingSessionMutation } from '../../../graphql/sessions/startParkingSessionMutation';
import { GetOngoingParkingSessionQuery } from '../../../graphql/sessions/getOngoingParkingSessionQuery';
import { UpdateEventMutation } from '../../../graphql/events/updateEventMutation';
import { useAuth } from '../../../lib/auth';
import { GateOption, ParkingType } from '../../../utils/utilTypes';
import { toastManager } from '../../../utils/utilsToast';
import { CardParkingDetailLoadingSkeleton } from './Skeleton';
import { ButtonsSection } from './components/ButtonsSection/ButtonsSection';
import { QuestsSection } from './components/QuestsSection/QuestsSection';
import { SubscriptionsPlansSection } from './components/SubscriptionPlansSection/SubscriptionsPlansSection';
import { CardParkingDetailProps } from './props';
import { buildParkingGateOptions } from '../../../utils/utilGates';
import {
  getParkingOrganizationName,
  getUnacceptedConsentPolicies,
  getUnlockErrorMessage,
} from '../../../utils/utilsParking';
import { OrganizationTag } from '../OrganizationTag/OrganizationTag';
import { ParkingTypeTag } from '../ParkingTypeTag';
import { useParkingBookingSlice } from '../../../slices/useParkingBookingSlice';
import { useSessionsSlice } from '../../../slices/useSessionsSlice';
import { ReportIssueModal } from '../ReportIssueModal';
import { SessionStartedOverlay } from '../SessionStartedOverlay/SessionStartedOverlay';
import { ConsentModal } from '../ConsentModal';

export const CardParkingDetail = ({
  parking,
  isLoading,
  handleStartPolling,
  isPollingForUnlockEvent,
  userSubscriptions,
}: CardParkingDetailProps) => {
  const [unlockParkingById, { loading: isLoadingUnlocking, error }] =
    useMutation(UnlockParkingByIdMutation);
  const [requestAccess, { loading: isLoadingRequestAccess }] = useMutation(
    RequestAccessToParkingMutation,
  );
  const [subscribe] = useMutation(SubscribeToPlanMutation);
  const [startParkingSession] = useMutation(StartParkingSessionMutation, {
    refetchQueries: [
      GetParkingByIdQuery,
      GetOngoingParkingSessionQuery,
      GetParkingsWithinDistance,
    ],
  });
  const [updateEvent] = useMutation(UpdateEventMutation);
  const { userBooking } = useParkingBookingSlice();
  const { ongoingSession } = useSessionsSlice();
  const [isParkingIssueModalShown, setIsParkingIssueModalShown] =
    useState<boolean>(false);
  const [isSessionStartedOverlayShown, setIsSessionStartedOverlayShown] =
    useState<boolean>(false);
  const [isConsentModalShown, setIsConsentModalShown] =
    useState<boolean>(false);

  const subscriptionPlans = parking?.subscriptionPlans || [];
  const isParkingSpotWithSubscriptionPlan = subscriptionPlans.length > 0;

  const router = useRouter();
  const { currentUser } = useAuth();
  const { t, i18n } = useTranslation();
  const activeLanguage = i18n.language;
  const gateAlias = router.query.gate as String;

  // Initialize selectedGate based on gateAlias
  const initialSelectedGate = useMemo(() => {
    if (gateAlias) {
      return buildParkingGateOptions(parking, currentUser, userBooking).find(
        (o) => o.label.toString() === gateAlias.toString(),
      );
    }
    return null;
  }, [parking, currentUser, gateAlias, userBooking]);

  const [selectedGate, setSelectedGate] = useState<
    GateOption | null | undefined
  >(initialSelectedGate);

  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = useState<
    string | null
  >(null);

  if (isLoading || !parking) return <CardParkingDetailLoadingSkeleton />;

  const organizationName = getParkingOrganizationName(parking);

  const isSingularLayout =
    parking?.layoutType === 'singular' ||
    parking?.layoutType === 'internal_testing_purposes';

  const userHasActiveSubscriptionPlan = subscriptionPlans.some((sp) => {
    return userSubscriptions.some((sub) => {
      return (
        sub.metadata.subscriptionPlanId === sp.id && sub.status === 'active'
      );
    });
  });

  const canShowGateSelector = () => {
    if (isSingularLayout) {
      return false;
    }

    if (parking.isInUnlockRadius) {
      if (isParkingSpotWithSubscriptionPlan) {
        return userHasActiveSubscriptionPlan;
      }
      return true;
    }

    return false;
  };

  const getGateValue = (
    selectedGate: GateOption | null | undefined,
    parking: ParkingType,
  ) => {
    if (selectedGate) {
      return selectedGate.value;
    }

    if (
      parking.layoutType === 'singular' &&
      parking.networkingType === 'mqtt'
    ) {
      const options = buildParkingGateOptions(
        parking,
        currentUser,
        userBooking,
      );
      return options[0].value;
    }
  };

  const handleSelectedGate = (gate: GateOption) => {
    setSelectedGate(gate);
  };

  const handleUnlock = async () => {
    if (!currentUser) {
      console.log('User is not logged in');
      return null;
    }

    const hasChildParkings = (parking.referencedBy?.length ?? 0) > 0;
    const willAutoStart = !hasChildParkings && !ongoingSession;

    try {
      const response = await unlockParkingById({
        variables: {
          parkingId: parking.id,
          type: 'manual',
          gate: getGateValue(selectedGate, parking),
        },
        refetchQueries: willAutoStart
          ? [GetParkingsWithinDistance]
          : [LatestEventQuery, GetParkingsWithinDistance],
      });

      const unlockedEvent = response?.data?.unlock;

      if (
        willAutoStart &&
        unlockedEvent?.gate?.id &&
        unlockedEvent?.parking?.id &&
        currentUser?.id
      ) {
        try {
          await startParkingSession({
            variables: {
              gateId: unlockedEvent.gate.id,
              parkingId: unlockedEvent.parking.id,
              userId: currentUser.id,
            },
          });

          await updateEvent({
            variables: {
              id: unlockedEvent.id,
              data: { isAknowledgedByUser: true },
            },
            refetchQueries: [LatestEventQuery],
          });

          setIsSessionStartedOverlayShown(true);
        } catch (err) {
          console.error(err);
          toastManager.couldNotStartSession(t);
        }
      }

      if (parking.networkingType === 'sim') {
        handleStartPolling();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onRequestAccess = async () => {
    try {
      await requestAccess({
        variables: {
          parkingId: parking.id,
        },
        refetchQueries: [GetParkingByIdQuery],
      });
      // TODO: Start polling for parking status?
      toastManager.requestAccessSuccess();
    } catch (err) {
      console.log(err);
    }
  };

  const onSubscribeClick = async () => {
    try {
      console.log('subscribing to subscription plan');
      const {
        data: { subscribeToPlan: checkoutLink },
      } = await subscribe({
        variables: {
          subscriptionPlanId: selectedSubscriptionPlan,
          parkingSpotId: parking.id,
          locale: activeLanguage.split('-')[0],
        },
      });

      if (checkoutLink.includes('https://')) {
        router.push(checkoutLink);
      } else {
        toastManager.subscriptionCreationError(checkoutLink);
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className="flex flex-1 h-full flex-col">
      <div className="rounded-t-general shadow-lg h-[160px] overflow-hidden mb-3 relative bg-black">
        {parking?.photo && (
          <Image
            priority
            className="brightness-30"
            src={parking?.photo?.image?.publicUrl}
            layout="fill"
            objectFit="cover"
            alt={parking?.photo?.image?.altText}
          />
        )}
        <div className="absolute w-full mx-auto transform  top-3">
          <div className="w-11/12 mx-auto flex flex-col">
            {parking.referenceParking && (
              <button
                onClick={() =>
                  router.push(
                    `?parkingId=${parking.referenceParking!.id}`,
                    undefined,
                    { shallow: true },
                  )
                }
                className="flex items-center gap-1 text-xs text-gray-300 mb-1 hover:text-white"
              >
                <span>&#8592;</span>
                <span className="truncate">
                  {parking.referenceParking.name}
                </span>
                <span className="text-gray-500">/</span>
                <span className="truncate text-white font-medium">
                  {parking.name}
                </span>
              </button>
            )}
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-lg font-bold text-white truncate min-w-0">
                {parking.name}
              </div>
            </div>
            <div className="flex justify-between items-start mt-2 gap-3">
              <div className="text-xs flex-1 min-w-0 max-w-[70%] text-gray-dark overflow-hidden whitespace-nowrap overflow-ellipsis">
                {parking.full_address || t('noAddressProvided')}
              </div>
              <div className="text-xs shrink-0 flex justify-end gap-2 text-white">
                {t('availableSpots')}
                {':'}{' '}
                <span className="font-bold">
                  {parking.capacity - parking.occupied}
                </span>
              </div>
            </div>

            <div className="flex w-full justify-between items-center mt-2">
              <div className="text-xs w-1/2 max-w-[70%] text-gray-dark overflow-hidden whitespace-nowrap overflow-ellipsis">
                <a
                  href={`https://www.google.com/maps/dir//${parking.lat},${parking.lng}`}
                  rel="noreferrer"
                  target="_blank"
                  className="underline"
                >
                  {' '}
                  {t('getDirections')}{' '}
                </a>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {/* <ParkingTypeTag type={parking.type} /> */}
                {organizationName && (
                  <OrganizationTag name={organizationName} />
                )}
              </div>
            </div>
            {(parking.links?.length ?? 0) > 0 && (
              <div className="flex justify-start gap-2 mt-4">
                {parking.links!.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs border px-2 py-1 rounded-md text-white"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 pb-4 flex flex-col">
        <QuestsSection
          quests={parking?.parkingQuests || []}
          userCompletedQuests={currentUser?.completedQuests || []}
        />

        <SubscriptionsPlansSection
          selectedSubscriptionPlan={selectedSubscriptionPlan}
          subscriptionPlans={subscriptionPlans}
          userSubscriptions={userSubscriptions}
          userHasActiveSubscriptionPlan={userHasActiveSubscriptionPlan}
          onSelect={setSelectedSubscriptionPlan}
        />

        {canShowGateSelector() ? (
          <div className="mt-4">
            <GateSelector
              parking={parking}
              selectedGate={selectedGate}
              onSelectGate={handleSelectedGate}
            />
          </div>
        ) : null}
        <div className="flex flex-1 flex-col justify-end mt-2">
          <ButtonsSection
            currentUser={currentUser}
            parking={parking}
            selectedSubscriptionPlan={selectedSubscriptionPlan}
            selectedGate={selectedGate}
            ongoingSession={ongoingSession}
            userSubscriptions={userSubscriptions}
            isLoadingUnlocking={isLoadingUnlocking}
            isLoadingRequestAccess={isLoadingRequestAccess}
            isPollingForUnlockEvent={isPollingForUnlockEvent}
            isSingularLayout={isSingularLayout}
            onRequestAccess={onRequestAccess}
            onSubscribeClick={onSubscribeClick}
            onOpenConsentModal={() => setIsConsentModalShown(true)}
            handleUnlock={handleUnlock}
            onReportIssue={() => setIsParkingIssueModalShown(true)}
          />
          {error && (
            <div className="text-xs mx-auto mt-2 text-danger text-center">
              {getUnlockErrorMessage(t, error)}
            </div>
          )}
        </div>
      </div>
      <ReportIssueModal
        isOpen={isParkingIssueModalShown}
        onClose={() => setIsParkingIssueModalShown(false)}
      />
      <ConsentModal
        isOpen={isConsentModalShown}
        onClose={() => setIsConsentModalShown(false)}
        policies={getUnacceptedConsentPolicies(currentUser, parking)}
      />
      {isSessionStartedOverlayShown && (
        <SessionStartedOverlay
          parkingType={parking.type}
          onDismiss={() => setIsSessionStartedOverlayShown(false)}
        />
      )}
    </div>
  );
};
